import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
//@ts-ignore
import { Map, Marker } from 'react-leaflet';
import { GenericMapTileLayer } from '../../components/mapComponent';
import { EVENT_LOGS_TYPES } from '../../constants';
import { Conversation, Message } from '../../hooks/useChat';
import {
  ChatContextHardware,
  ChatContextRental,
  RentalUnlocks,
  fetchConversationHistory,
  fetchRentalUnlocks,
  getHardwareEventLogs,
} from '../../hooks/chat/api';
import { mapSession, riderLabel, systemLabel } from '../../hooks/chat/mappers';
import { useConversationContext } from '../../hooks/chat/useConversationContext';
import { IHardwareEvent } from '../../ts/interface/hardware.interface';
import { ConversationControls } from './ConversationControls';
import { PanelSection } from './PanelSection';
import { QuickActions } from './QuickActions';
import { SupportTicketSection } from './SupportTicketSection';
import { formatAgo, formatDateTime, statusLabel } from './format';
import { ChatTagGlossary, HardwareFamily, tagLabel } from './tagGlossary';
import { describeClientContext } from './clientContext';
import { summarizeUnlocks } from './unlocks';

interface Props {
  conversation: Conversation;
  /** The newest rider message's app context, and when it was sent. */
  clientContext?: { context: Record<string, unknown>; at: string } | null;
  myAdminId: string | null;
  adminEmail: string;
  /** Opens the End conversation dialog (the same one as the header icon). */
  onEndConversation: () => void;
  /** Opens a past session of the same rider (read-only when closed). */
  onOpenConversation?: (conversation: Conversation) => void;
  assignConversation: (convId: string, adminId: string | null) => Promise<void>;
  onAddTag: (convId: string, tag: string) => Promise<void>;
  onRemoveTag: (convId: string, tag: string) => Promise<void>;
  onSetPriority: (convId: string, priority: 'normal' | 'urgent') => Promise<void>;
  /** CS's glossary, for the tag picker and the labels of earlier conversations. */
  glossary: ChatTagGlossary | null;
  glossaryError?: string | null;
  /** The session's rental family, for the picker's filter; undefined while unknown. */
  hardwareFamily: HardwareFamily | null | undefined;
  /** The thread, for a support ticket's content. */
  messages?: Message[];
  /** The signed-in agent's name, on a support ticket. */
  adminName?: string;
  /** Posts an internal note (a transfer's, a ticket's). */
  onAddNote?: (convId: string, note: string) => Promise<void>;
  /** An assistant session: shown, not acted on, until an agent takes it over. */
  readOnly?: boolean;
  /**
   * ant refused this admin's writes on the session: no assign, priority,
   * tags or End conversation. A ticket is another permission, so it stays,
   * without the chat note it would post.
   */
  writeRefused?: boolean;
  /**
   * Another agent has the chat (assignee.ts): no End conversation, no
   * Assign to me (the composer's Take over is how it becomes this agent's,
   * with a note saying so) and no Unassign. Reassign, priority, tags and
   * tickets stay.
   */
  heldByOther?: boolean;
}

const EVENT_COLOR: Record<string, string> = {
  success: 'success.main',
  error: 'error.main',
  warning: 'warning.main',
  info: 'info.main',
};

const eventColor = (type: string) =>
  EVENT_COLOR[EVENT_LOGS_TYPES.find((e) => e.value === type)?.color ?? ''] ??
  'neutral.400';

const hardwareLabel = (hardware: ChatContextHardware) =>
  `${hardware.hardwareProduct?.name ?? 'Hardware'} #${
    hardware.numberText ?? hardware.number ?? ''
  }`;

function RentalSection({
  rental,
  hardware,
  loading,
  error,
  onReload,
}: {
  rental: ChatContextRental | null;
  hardware: ChatContextHardware | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}) {
  return (
    <PanelSection
      title="Rental"
      testId="rental-section"
      action={
        <Tooltip title="Reload">
          <span>
            <IconButton
              size="small"
              onClick={onReload}
              disabled={loading}
              aria-label="Reload rental"
            >
              {loading ? (
                <CircularProgress size={14} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      {loading && !rental ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : error ? (
        <Typography variant="body2" color="error">
          Could not load the rental. {error}
        </Typography>
      ) : rental ? (
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Link
              href={`/#/rentals/${rental.id}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              Open rental <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
            <Chip
              size="small"
              label={rental.endTime ? 'Ended' : 'Active'}
              color={rental.endTime ? 'default' : 'success'}
              variant="outlined"
            />
          </Stack>
          {hardware && (
            <Typography variant="body2">
              <Link
                href={`/#/hardware/${hardware.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {hardwareLabel(hardware)}
              </Link>
            </Typography>
          )}
          {rental.startTime && (
            <Typography variant="body2" color="text.secondary">
              Started {formatDateTime(rental.startTime)}
            </Typography>
          )}
          {rental.endTime && (
            <Typography variant="body2" color="text.secondary">
              Ended {formatDateTime(rental.endTime)}
            </Typography>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No rental linked to this conversation.
        </Typography>
      )}
    </PanelSection>
  );
}

const TONE_COLOR = { warning: 'warning.dark', error: 'error.main' } as const;

/** What the rider's app reported with their latest message. */
function ClientContextSection({
  context,
  at,
}: {
  context: Record<string, unknown>;
  at: string;
}) {
  const rows = describeClientContext(context);
  if (rows.length === 0) return null;
  return (
    <PanelSection
      title="Rider's app"
      testId="client-context-section"
      action={
        <Tooltip title={`Sent with the message of ${formatDateTime(at)}`}>
          <Typography variant="caption" color="text.secondary">
            {formatAgo(at) === 'just now' ? 'just now' : `${formatAgo(at)} ago`}
          </Typography>
        </Tooltip>
      }
    >
      <Box
        component="dl"
        sx={{
          m: 0,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: 1.5,
          rowGap: 0.5,
        }}
      >
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <Typography component="dt" variant="body2" color="text.secondary">
              {row.label}
            </Typography>
            <Tooltip title={row.detail ?? ''} disableHoverListener={!row.detail}>
              <Typography
                component="dd"
                variant="body2"
                sx={{
                  m: 0,
                  minWidth: 0,
                  wordBreak: 'break-word',
                  color: row.tone ? TONE_COLOR[row.tone] : 'text.primary',
                  fontWeight: row.tone ? 600 : undefined,
                }}
                data-testid={`client-context-${row.label}`}
              >
                {row.value}
              </Typography>
            </Tooltip>
          </React.Fragment>
        ))}
      </Box>
    </PanelSection>
  );
}

/** How many events of the rental's unlock log the panel lists. */
const UNLOCK_EVENTS_SHOWN = 10;

/** The anchored rental's unlock attempts and successes, per unit, newest first. */
function UnlockAttemptsSection({ rentalId }: { rentalId: string }) {
  const [data, setData] = useState<RentalUnlocks | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetchRentalUnlocks(rentalId)
      .then((next) => {
        if (!cancelled) setData(next);
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rentalId, nonce]);

  const summary = summarizeUnlocks(data);
  return (
    <PanelSection
      title="Unlock attempts"
      testId="unlocks-section"
      action={
        <Tooltip title="Reload">
          <span>
            <IconButton
              size="small"
              onClick={() => setNonce((n) => n + 1)}
              disabled={loading}
              aria-label="Reload unlock attempts"
            >
              {loading ? (
                <CircularProgress size={14} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      {failed ? (
        <Typography variant="body2" color="error">
          Could not load the unlock attempts.
        </Typography>
      ) : summary.events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {loading ? 'Loading…' : 'No unlock attempts during this rental'}
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          <Typography variant="body2" data-testid="unlocks-summary">
            {`${summary.attempts} attempt${summary.attempts === 1 ? '' : 's'}, ${
              summary.successes
            } success${summary.successes === 1 ? '' : 'es'}`}
            {summary.successRate !== null && ` · ${summary.successRate}% succeeded`}
          </Typography>
          {summary.events.slice(0, UNLOCK_EVENTS_SHOWN).map((ev) => (
            <Stack key={ev.id} direction="row" gap={1} alignItems="flex-start">
              <FiberManualRecordIcon
                sx={{
                  fontSize: 10,
                  mt: 0.6,
                  color: ev.type === 'success' ? 'success.main' : 'warning.main',
                }}
              />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {ev.type === 'success' ? 'Unlocked' : 'Attempt'}
                {ev.hardwareLabel && ` · ${ev.hardwareLabel}`}
              </Typography>
              <Tooltip title={formatDateTime(ev.at)}>
                <Typography variant="caption" color="text.secondary">
                  {formatAgo(ev.at)}
                </Typography>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}
    </PanelSection>
  );
}

/** The hardware the rider's messages name, when it is not the rental's. */
function MessageHardwareSection({ hardware }: { hardware: ChatContextHardware }) {
  return (
    <PanelSection title="Hardware the rider wrote about" testId="message-hardware">
      <Typography variant="body2">
        <Link
          href={`/#/hardware/${hardware.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {hardwareLabel(hardware)}
        </Link>
      </Typography>
      {hardware.system?.name && (
        <Typography variant="body2" color="text.secondary">
          {hardware.system.name}
        </Typography>
      )}
    </PanelSection>
  );
}

function EventsSection({
  hardware,
  rental,
}: {
  hardware: ChatContextHardware;
  rental: ChatContextRental | null;
}) {
  const [events, setEvents] = useState<IHardwareEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const hardwareId = hardware.id;
  const after = rental?.startTime ?? undefined;
  const before = rental?.endTime ?? undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await getHardwareEventLogs(hardwareId, {
        limit: 15,
        after_created_on: after,
        before_created_on: before,
      });
      setEvents(res.data);
    } catch (_) {
      setFailed(true);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [hardwareId, after, before]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PanelSection
      title={rental ? 'Hardware events during the rental' : 'Recent hardware events'}
      testId="events-section"
      action={
        <Tooltip title="Reload">
          <span>
            <IconButton
              size="small"
              onClick={load}
              disabled={loading}
              aria-label="Reload events"
            >
              {loading ? (
                <CircularProgress size={14} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      {failed ? (
        <Typography variant="body2" color="error">
          Could not load the events.
        </Typography>
      ) : events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {loading ? 'Loading…' : 'No events'}
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {events.map((ev) => (
            <Stack key={ev.id} direction="row" gap={1} alignItems="flex-start">
              <FiberManualRecordIcon
                sx={{ fontSize: 10, mt: 0.6, color: eventColor(ev.type) }}
              />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {ev.type || 'event'}
              </Typography>
              <Tooltip title={formatDateTime(ev.created_on)}>
                <Typography variant="caption" color="text.secondary">
                  {formatAgo(ev.created_on)}
                </Typography>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}
    </PanelSection>
  );
}

function HistorySection({
  conversation,
  glossary,
  onOpen,
}: {
  conversation: Conversation;
  glossary: ChatTagGlossary | null;
  onOpen?: (conversation: Conversation) => void;
}) {
  const [history, setHistory] = useState<Conversation[] | null>(null);
  const [failed, setFailed] = useState(false);
  const sessionId = conversation.id;

  useEffect(() => {
    let cancelled = false;
    setHistory(null);
    setFailed(false);
    fetchConversationHistory(sessionId, 10)
      .then((sessions) => {
        if (cancelled) return;
        setHistory(sessions.map(mapSession).filter((c) => c.id !== sessionId));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <PanelSection title="Earlier conversations" testId="history-section">
      {failed ? (
        <Typography variant="body2" color="error">
          Could not load earlier conversations.
        </Typography>
      ) : history === null ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : history.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          This is the rider&apos;s first conversation.
        </Typography>
      ) : (
        <List dense disablePadding sx={{ mx: -2 }}>
          {history.map((h) => (
            <ListItemButton
              key={h.id}
              onClick={onOpen ? () => onOpen(h) : undefined}
              disabled={!onOpen}
              sx={{ px: 2, display: 'block' }}
            >
              <Stack direction="row" justifyContent="space-between" gap={1}>
                <Typography variant="body2">{formatDateTime(h.created_on)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {statusLabel(h.status)}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {h.resolution_note ||
                  (h.tags?.length
                    ? h.tags.map((t) => tagLabel(glossary, t)).join(', ')
                    : systemLabel(h))}
              </Typography>
            </ListItemButton>
          ))}
        </List>
      )}
    </PanelSection>
  );
}

/** The right-hand panel: who the rider is, what they are riding, what can be done. */
export function ActionPanel({
  conversation,
  clientContext = null,
  myAdminId,
  adminEmail,
  onEndConversation,
  onOpenConversation,
  assignConversation,
  onAddTag,
  onRemoveTag,
  onSetPriority,
  glossary,
  glossaryError = null,
  hardwareFamily,
  messages = [],
  adminName = '',
  onAddNote,
  readOnly = false,
  writeRefused = false,
  heldByOther = false,
}: Props) {
  const context = useConversationContext(
    conversation.context_rental_id,
    conversation.context_hardware_id
  );
  const closed = conversation.status === 'closed';
  const location =
    typeof conversation.user_lat === 'number' && typeof conversation.user_lng === 'number'
      ? { lat: conversation.user_lat, lng: conversation.user_lng }
      : null;

  return (
    <Box
      sx={{
        width: 320,
        flexShrink: 0,
        borderLeft: 1,
        borderColor: 'divider',
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
      data-testid="action-panel"
    >
      <Box
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        data-testid="action-panel-scroll"
      >
        <PanelSection title="Rider">
          <Typography variant="subtitle2">{riderLabel(conversation)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {conversation.user_phone}
          </Typography>
          <Chip
            size="small"
            sx={{ mt: 1 }}
            label={systemLabel(conversation)}
            color={conversation.system_id ? 'primary' : 'default'}
            variant="outlined"
          />
        </PanelSection>

        <ConversationControls
          conversation={conversation}
          myAdminId={myAdminId}
          glossary={glossary}
          glossaryError={glossaryError}
          hardwareFamily={hardwareFamily}
          onAssign={assignConversation}
          onSetPriority={onSetPriority}
          onAddTag={onAddTag}
          onRemoveTag={onRemoveTag}
          onAddNote={onAddNote}
          readOnly={readOnly || writeRefused}
          heldByOther={heldByOther}
        />

        <RentalSection
          rental={context.rental}
          hardware={context.rentalHardware}
          loading={context.loading}
          error={context.error}
          onReload={context.reload}
        />

        {context.rental && <UnlockAttemptsSection rentalId={context.rental.id} />}

        {clientContext && (
          <ClientContextSection context={clientContext.context} at={clientContext.at} />
        )}

        {context.messageHardware && (
          <MessageHardwareSection hardware={context.messageHardware} />
        )}

        {location && (
          <PanelSection title="Where the rider wrote from">
            <Box
              sx={{
                height: 160,
                borderRadius: 1,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Map
                center={[location.lat, location.lng]}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                zoomControl={false}
                attributionControl={false}
              >
                <GenericMapTileLayer />
                <Marker position={[location.lat, location.lng]} />
              </Map>
            </Box>
          </PanelSection>
        )}

        {/* The rental's unit during the rental; else the one the rider named. */}
        {context.rentalHardware ? (
          <EventsSection hardware={context.rentalHardware} rental={context.rental} />
        ) : (
          context.messageHardware && (
            <EventsSection hardware={context.messageHardware} rental={null} />
          )
        )}

        {!closed && !readOnly && (
          <QuickActions
            conversation={conversation}
            rental={context.rental}
            rentalHardware={context.rentalHardware}
            messageHardware={context.messageHardware}
            adminEmail={adminEmail}
            loading={context.loading}
          />
        )}

        <SupportTicketSection
          conversation={conversation}
          messages={messages}
          rental={context.rental}
          agentName={adminName}
          onAddNote={writeRefused ? undefined : onAddNote}
        />

        <HistorySection
          conversation={conversation}
          glossary={glossary}
          onOpen={onOpenConversation}
        />
      </Box>

      {/* Pinned below the scrolling sections: always in reach. */}
      {!closed && !readOnly && !writeRefused && !heldByOther && (
        <Box
          sx={{ p: 2, flexShrink: 0, borderTop: 1, borderColor: 'divider' }}
          data-testid="action-panel-footer"
        >
          <Button
            variant="outlined"
            color="error"
            fullWidth
            startIcon={<TaskAltIcon />}
            onClick={onEndConversation}
          >
            End conversation
          </Button>
        </Box>
      )}
    </Box>
  );
}
